import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { EmployeePerformanceData } from '../types/employeePerformance.ts';
import { formatDate } from '../utils/dateUtils.ts';
import { shouldMaskDirectorData, isSelfEmployee } from '../utils/teamPerformanceUtils.ts';

interface ExportEmployeePerformanceData {
  employeePerformanceData: EmployeePerformanceData[];
  currentDate: string;
  userRole?: string;
  userTeamName?: string | null;
  /** 로그인 계정에 연결된 직원 id — 실장 본인 데이터 마스킹 해제 판정용 */
  userEmployeeId?: string;
}

interface UseEmployeePerformanceExporterReturn {
  exportToExcel: (data: ExportEmployeePerformanceData, fileName?: string) => void;
  isExporting: boolean;
  error: string | null;
}

/**
 * 직원별 성과 현황 엑셀 파일 내보내기 훅
 * 민감한 재무 정보(세금, 지급액)는 제외하고 성과 관련 데이터만 내보내기
 */
export const useEmployeePerformanceExporter = (): UseEmployeePerformanceExporterReturn => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 직원 성과 데이터를 엑셀용 형태로 변환
   * 민감한 필드(세금, 실지급액) 제외
   * @param employeeData - 직원별 성과 데이터
   * @param userRole - 사용자 역할
   * @param userTeamName - 사용자 팀명 (직원 역할일 때 사용)
   */
  const convertToExcelFormat = useCallback((
    employeeData: EmployeePerformanceData[],
    userRole?: string,
    userTeamName?: string | null,
    userEmployeeId?: string
  ) => {
    return employeeData.map((performance, index) => {
      // 직원 역할이고 다른 팀인 경우 또는 실장 데이터를 admin이 아닌 사용자가 볼 경우 마스킹
      const shouldMask = (userRole === 'employee' && userTeamName !== performance.teamName)
        || shouldMaskDirectorData(performance.position, userRole, isSelfEmployee(userEmployeeId, performance.employeeId));

      return {
        '번호': index + 1,
        '직원명': shouldMask ? '****' : performance.employeeName,
        '팀': performance.teamName,
        '직책': shouldMask ? '****' : performance.position,
        '기본급': shouldMask ? '****' : performance.baseSalary,
        '수당(초과+개별)': shouldMask ? '****' : performance.payrollAllowances,
        '인센티브': shouldMask ? '****' : performance.incentive || 0,
        '급여관리 총급여': shouldMask ? '****' : performance.payrollTotalSalary,
        '팀 달성률(%)': shouldMask ? '****' : Math.round(performance.performanceMetrics.teamAchievementRate * 100),
        // 민감한 정보는 의도적으로 제외: 세금, 실지급액
        // 개인정보 보호를 위해 퇴직금 관련 정보도 제외
        // 팀 매출 관련 정보도 민감한 정보로 제외: 팀 목표매출, 팀 실제매출, 팀 초과매출, 팀 미달성매출
      };
    });
  }, []);

  /**
   * 팀별 집계 데이터 생성
   * @param employeeData - 직원별 성과 데이터
   * @param userRole - 사용자 역할
   * @param userTeamName - 사용자 팀명
   */
  const createTeamSummaryData = useCallback((
    employeeData: EmployeePerformanceData[],
    userRole?: string,
    userTeamName?: string | null,
    userEmployeeId?: string
  ) => {
    // 팀별로 데이터 그룹화
    const teamGroups = employeeData.reduce((groups, performance) => {
      const teamName = performance.teamName;

      // 직원 역할이고 다른 팀인 경우 제외
      if (userRole === 'employee' && userTeamName !== teamName) {
        return groups;
      }

      if (!groups[teamName]) {
        groups[teamName] = [];
      }
      groups[teamName].push(performance);
      return groups;
    }, {} as Record<string, EmployeePerformanceData[]>);

    // 팀별 집계 생성
    return Object.entries(teamGroups).map(([teamName, teamMembers], index) => {
      // 팀별 고유 성과 지표 (첫 번째 멤버 기준, 모든 멤버가 동일한 팀 성과를 가짐)
      const teamMetrics = teamMembers[0]?.performanceMetrics;

      return {
        '번호': index + 1,
        '팀명': teamName,
        '팀원 수': teamMembers.length,
        '팀 기본급 합계': teamMembers.reduce((sum, member) => sum + member.baseSalary, 0),
        '팀 수당 합계': teamMembers.reduce((sum, member) => sum + member.payrollAllowances, 0),
        '팀 인센티브 합계': teamMembers.reduce((sum, member) => sum + (member.incentive || 0), 0),
        '팀 총급여 합계': teamMembers.reduce((sum, member) => sum + member.payrollTotalSalary, 0),
        '팀 달성률(%)': teamMetrics ? Math.round(teamMetrics.teamAchievementRate * 100) : 0,
        // 팀 매출 관련 정보는 민감한 정보로 제외: 팀 목표매출, 팀 실제매출, 팀 초과매출, 팀 미달성매출
      };
    });
  }, []);

  /**
   * 엑셀 파일로 내보내기
   * @param data - 내보낼 데이터
   * @param fileName - 파일명 (옵션)
   */
  const exportToExcel = useCallback(async (data: ExportEmployeePerformanceData, fileName?: string) => {
    setIsExporting(true);
    setError(null);

    try {
      // 새 워크북 생성
      const workbook = XLSX.utils.book_new();

      // 1. 직원별 성과 상세 데이터 시트
      const detailData = convertToExcelFormat(
        data.employeePerformanceData,
        data.userRole,
        data.userTeamName,
        data.userEmployeeId
      );

      if (detailData.length > 0) {
        const detailWorksheet = XLSX.utils.json_to_sheet(detailData);

        // 컬럼 너비 설정
        const columnWidths = [
          { wch: 6 },  // 번호
          { wch: 10 }, // 직원명
          { wch: 8 },  // 팀
          { wch: 8 },  // 직책
          { wch: 12 }, // 기본급
          { wch: 15 }, // 수당
          { wch: 12 }, // 인센티브
          { wch: 15 }, // 총급여
          { wch: 12 }, // 달성률
        ];
        detailWorksheet['!cols'] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, detailWorksheet, '직원별성과상세');
      }

      // 2. 팀별 집계 시트
      const teamSummaryData = createTeamSummaryData(
        data.employeePerformanceData,
        data.userRole,
        data.userTeamName,
        data.userEmployeeId
      );

      if (teamSummaryData.length > 0) {
        const teamSummaryWorksheet = XLSX.utils.json_to_sheet(teamSummaryData);

        // 컬럼 너비 설정
        const teamColumnWidths = [
          { wch: 6 },  // 번호
          { wch: 10 }, // 팀명
          { wch: 8 },  // 팀원 수
          { wch: 15 }, // 기본급 합계
          { wch: 15 }, // 수당 합계
          { wch: 15 }, // 인센티브 합계
          { wch: 15 }, // 총급여 합계
          { wch: 12 }, // 달성률
        ];
        teamSummaryWorksheet['!cols'] = teamColumnWidths;

        XLSX.utils.book_append_sheet(workbook, teamSummaryWorksheet, '팀별집계');
      }

      // 3. 요약 시트 생성
      const filteredData = data.userRole === 'employee'
        ? data.employeePerformanceData.filter(p => p.teamName === data.userTeamName)
        : data.employeePerformanceData;

      const summaryData = [
        { '항목': '데이터 생성일시', '값': new Date().toLocaleString() },
        { '항목': '조회 범위', '값': data.userRole === 'employee' ? `${data.userTeamName} 팀` : '전체' },
        { '항목': '직원 수', '값': filteredData.length },
        { '항목': '팀 수', '값': new Set(filteredData.map(p => p.teamName)).size },
        { '항목': '총 기본급', '값': filteredData.reduce((sum, p) => sum + p.baseSalary, 0) },
        { '항목': '총 수당', '값': filteredData.reduce((sum, p) => sum + p.payrollAllowances, 0) },
        { '항목': '총 인센티브', '값': filteredData.reduce((sum, p) => sum + (p.incentive || 0), 0) },
        { '항목': '총 급여관리 총급여', '값': filteredData.reduce((sum, p) => sum + p.payrollTotalSalary, 0) },
        { '항목': '주의사항', '값': '민감한 재무정보(세금, 실지급액, 팀 매출 관련 정보)는 개인정보 보호를 위해 제외됨' },
      ];

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      summaryWorksheet['!cols'] = [{ wch: 20 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, '요약');

      // 파일명 생성 (YYYY-MM-DD직원성과현황.xlsx 형식)
      const finalFileName = fileName || `${data.currentDate}직원성과현황.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(workbook, finalFileName);

      console.log('✅ [Employee Performance Export] 엑셀 파일 다운로드 완료:', finalFileName);

    } catch (exportError) {
      const errorMessage = exportError instanceof Error ? exportError.message : '직원 성과 엑셀 내보내기 중 오류가 발생했습니다';
      setError(errorMessage);
      console.error('❌ [Employee Performance Export] 엑셀 내보내기 오류:', exportError);
      throw new Error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  }, [convertToExcelFormat, createTeamSummaryData]);

  return {
    exportToExcel,
    isExporting,
    error
  };
};

// Default export
export default useEmployeePerformanceExporter;